import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { useAuth, type AppRole } from "@/contexts/AuthContext";

/* Brand tokens — kept inline to match Landing/dark pages */
const C = {
  bg: "#050505",
  surface: "#0d0d0d",
  card: "#0e0e0e",
  border: "#1f1f1f",
  borderHi: "#2a2a2a",
  teal: "#12cfc0",
  tealLt: "#58e8de",
  text: "#f5f5f5",
  textHi: "#ffffff",
  sub: "#a1a1aa",
  coral: "#f0705f",
};

function roleLabel(role: AppRole | null): string {
  if (role === "admin") return "an admin";
  if (role === "coach") return "a coach";
  if (role === "client") return "a client";
  return "signed out";
}

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealLt})`,
          display: "grid",
          placeItems: "center",
          color: "#031518",
          fontWeight: 900,
          fontFamily: "Outfit, system-ui, sans-serif",
          fontSize: 18,
          boxShadow: `0 6px 22px -10px ${C.teal}`,
        }}
      >
        C
      </div>
      <span
        style={{
          color: C.textHi,
          fontFamily: "Outfit, system-ui, sans-serif",
          fontWeight: 800,
          letterSpacing: 0.4,
          fontSize: 18,
        }}
      >
        Coach AI
      </span>
    </div>
  );
}

export default function Forbidden() {
  const { role, user } = useAuth();
  const location = useLocation();
  const attempted = (location.state as { from?: string } | null)?.from;

  // Per product spec: authed → /app, unauthed → /auth.
  const homeTo = user ? "/app" : "/auth";
  const homeLabel = user ? "Back to dashboard" : "Sign in";

  useEffect(() => {
    if (user) {
      toast.error("Not authorized", {
        description: attempted
          ? `${attempted} isn't available to ${roleLabel(role)}.`
          : `That page isn't available to ${roleLabel(role)}.`,
      });
    } else {
      toast("Please log in", {
        description: attempted
          ? `Sign in to continue to ${attempted}.`
          : "Sign in to continue.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Not authorized · Coach AI</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div
        style={{
          minHeight: "100vh",
          background: `radial-gradient(1200px 600px at 50% -10%, ${C.teal}10, transparent 60%), ${C.bg}`,
          color: C.text,
          fontFamily: "DM Sans, system-ui, sans-serif",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: 1200,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Link to="/" style={{ textDecoration: "none" }}>
            <Logo />
          </Link>
        </header>

        <main
          style={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            padding: "24px",
          }}
        >
          <div
            style={{
              maxWidth: 480,
              width: "100%",
              textAlign: "center",
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: "40px 28px",
              boxShadow: "0 30px 60px -30px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                margin: "0 auto 20px",
                borderRadius: 16,
                background: `${C.coral}18`,
                border: `1px solid ${C.coral}40`,
                display: "grid",
                placeItems: "center",
                color: C.coral,
              }}
            >
              <ShieldAlert size={30} strokeWidth={2.2} />
            </div>

            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 2,
                color: C.sub,
                textTransform: "uppercase",
                margin: 0,
                marginBottom: 8,
              }}
            >
              Error 403
            </p>
            <h1
              style={{
                fontFamily: "Outfit, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 30,
                color: C.textHi,
                margin: 0,
                marginBottom: 12,
                letterSpacing: -0.4,
              }}
            >
              You don't have access to this page
            </h1>
            <p
              style={{
                color: C.sub,
                fontSize: 15,
                lineHeight: 1.55,
                margin: 0,
                marginBottom: 24,
              }}
            >
              {user
                ? "Your account doesn't have permission to view this area. Head back to your dashboard to keep going."
                : "You'll need to sign in to your Coach AI account to see this page."}
              {attempted && (
                <span
                  style={{
                    display: "block",
                    marginTop: 10,
                    fontSize: 12,
                    opacity: 0.65,
                  }}
                >
                  Tried to open <code style={{ color: C.tealLt }}>{attempted}</code>
                </span>
              )}
            </p>

            <Link
              to={homeTo}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 22px",
                borderRadius: 12,
                background: `linear-gradient(135deg, ${C.teal}, ${C.tealLt})`,
                color: "#031518",
                fontWeight: 700,
                fontFamily: "Outfit, system-ui, sans-serif",
                textDecoration: "none",
                boxShadow: `0 10px 30px -12px ${C.teal}`,
              }}
            >
              {homeLabel}
              <ArrowRight size={18} />
            </Link>

            <div style={{ marginTop: 18 }}>
              <Link
                to="/"
                style={{
                  color: C.sub,
                  fontSize: 13,
                  textDecoration: "none",
                  borderBottom: `1px dashed ${C.borderHi}`,
                  paddingBottom: 1,
                }}
              >
                Or return to the homepage
              </Link>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
